import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useDispatch, useSelector } from "react-redux";
import { getKpiTypeColor, getUserAvatarColor, formatQuarterRange } from "../../../utils/kpiColors";

const CustomTrendTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900 text-white text-xs px-3 py-2 rounded-xl shadow-xl border border-slate-800">
        <div className="font-bold text-slate-100">{data.label || data.name}</div>
        {data.periodDetail && (
          <div className="text-[10px] text-slate-400 font-medium mt-0.5">{data.periodDetail}</div>
        )}
        <div className="text-xs font-bold text-emerald-400 mt-1 flex items-baseline gap-1">
          <span>{data.score}</span>
          <span className="text-[10px] text-slate-400 font-normal">pts</span>
        </div>
      </div>
    );
  }
  return null;
};

const KPIDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dashboard, leaderboard, trend, categoryBreakdown, taskTypes, upcomingRecurring, activityLog, loading } = useSelector((state) => state.kpi);
  const userInfoData = useSelector((state) => state.auth?.userInfoData);
  const userInfo = useSelector((state) => state.auth?.userInfo);

  const [trendPeriod, setTrendPeriod] = React.useState("month");

  const handlePeriodChange = (period) => {
    setTrendPeriod(period);
    dispatch.kpi.getTrend({ period });
  };

  useEffect(() => {
    dispatch.kpi.getDashboard();
    dispatch.kpi.getLeaderboard();
    dispatch.kpi.getTrend({ period: trendPeriod });
    dispatch.kpi.getCategoryBreakdown();
    dispatch.kpi.getTaskTypes();
    dispatch.kpi.getUpcomingRecurring();
    dispatch.kpi.getActivityLog({ limit: 10 });
  }, [dispatch]);

  const leaderboardData = React.useMemo(() => {
    const list = [...(leaderboard || [])];
    return list.sort((a, b) => {
      const rA = a.rank !== undefined && a.rank !== null ? Number(a.rank) : 9999;
      const rB = b.rank !== undefined && b.rank !== null ? Number(b.rank) : 9999;
      if (rA !== rB) return rA - rB;

      const pA = a.total_points !== undefined ? Number(a.total_points) : (a.total_score !== undefined ? Number(a.total_score) : (a.monthly_score || 0));
      const pB = b.total_points !== undefined ? Number(b.total_points) : (b.total_score !== undefined ? Number(b.total_score) : (b.monthly_score || 0));
      return pB - pA;
    });
  }, [leaderboard]);

  const flattenedActivities = React.useMemo(() => {
    if (Array.isArray(activityLog) && activityLog.length > 0) {
      const items = [];
      activityLog.forEach(group => {
        if (Array.isArray(group.activities)) {
          group.activities.forEach(act => {
            items.push({ ...act, periodGroup: group.period });
          });
        } else if (group.activity_id || group._id || group.description) {
          items.push(group);
        }
      });
      if (items.length > 0) return items;
    }
    return dashboard?.activity || [];
  }, [activityLog, dashboard?.activity]);

  // Extract categories 100% strictly from GET /api/kpi/category-breakdown API endpoint
  const rawCategories = (Array.isArray(categoryBreakdown) && categoryBreakdown.length > 0)
    ? [...categoryBreakdown]
    : (dashboard?.category_breakdown || dashboard?.activity_by_category || dashboard?.categories || []);

  const totalCatPoints = rawCategories.reduce((sum, c) => {
    const p = c.points !== undefined ? Number(c.points) : (c.score !== undefined ? Number(c.score) : 0);
    return sum + (isNaN(p) ? 0 : p);
  }, 0);

  const getSafeStringVal = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    if (typeof val === "object") {
      return val.kpi_type_name || val.name || val.title || val.type || "";
    }
    return "";
  };

  const displayCategoryData = rawCategories.map((c, idx) => {
    const rawName = c.category_name || c.name || c.kpi_type_name || c.type;
    const name = getSafeStringVal(rawName) || "Misc";
    const pts = c.total_points !== undefined
      ? Number(c.total_points)
      : (c.points !== undefined ? Number(c.points) : (c.score !== undefined ? Number(c.score) : 0));

    const matchedTaskType = Array.isArray(taskTypes)
      ? taskTypes.find(tt => (tt.name || tt.kpi_type_name || "").toLowerCase().trim() === name.toLowerCase().trim() || String(tt._id) === String(c.category_id || c._id))
      : null;

    const hexColor = c.bg_color || c.color || matchedTaskType?.bg_color || matchedTaskType?.color;

    let pct = c.percentage !== undefined && c.percentage !== null ? Number(c.percentage) : 0;
    if (isNaN(pct)) pct = 0;

    if (!pct && totalCatPoints > 0 && pts > 0) {
      pct = Math.round((pts / totalCatPoints) * 100);
    }

    const colorObj = hexColor ? getKpiTypeColor(hexColor) : getKpiTypeColor(name, idx);

    return {
      name,
      points: isNaN(pts) ? 0 : pts,
      percentage: pct,
      hexColor: hexColor || colorObj.hex,
      colorObj
    };
  });

  // Parse dynamic summary & trend data from API response
  const rawTrend = (Array.isArray(trend) && trend.length > 0)
    ? trend
    : (dashboard?.trend || dashboard?.data?.trend || []);

  const trendData = rawTrend.map((item, index) => {
    const qInfo = formatQuarterRange(item, index, trendPeriod);
    let label = item.label || (item.day && item.month ? `${item.day} ${item.month}` : (item.month || item.name || item.date || ''));
    let name = item.label || label;
    let periodDetail = '';

    if (qInfo) {
      name = qInfo.range;
      label = qInfo.range;
      const qTitle = item.label || `Quarter ${qInfo.quarterNum}`;
      periodDetail = item.year ? `${qTitle} (${item.year})` : qTitle;
    } else {
      if (item.month && item.month.includes(',')) {
        periodDetail = item.year ? `${item.month} (${item.year})` : item.month;
      } else if (item.year && item.month && !label.includes(String(item.year))) {
        periodDetail = `${item.month} ${item.year}`;
      } else if (item.year && !label.includes(String(item.year))) {
        periodDetail = String(item.year);
      }
    }

    return {
      name,
      label,
      score: item.score !== undefined && item.score !== null ? item.score : 0,
      day: item.day,
      month: item.month,
      year: item.year,
      periodDetail
    };
  });

  const summary = dashboard?.summary || dashboard?.data?.summary || {};
console.log(summary,"summary")
  // Support both new KPI summary format (team_avg_kpi, efficiency_score, delivery_score, engagement_score) and legacy counts
  const teamAvgKpiObj = summary?.team_avg_kpi || {};
  const efficiencyObj = summary?.efficiency_score || {};
  const deliveryObj = summary?.delivery_score || {};
  const engagementObj = summary?.engagement_score || {};

 const totalPoints = teamAvgKpiObj.value !== undefined
  ? teamAvgKpiObj.value
  : (summary?.monthly_score !== undefined && summary?.monthly_score !== null
      ? summary.monthly_score
      : 0);
  const completedCount = efficiencyObj.value !== undefined ? efficiencyObj.value : (summary?.completed !== undefined ? summary.completed : 0);
  const inProgressCount = deliveryObj.value !== undefined ? deliveryObj.value : (summary?.in_progress !== undefined ? summary.in_progress : 0);
  const delayedCount = engagementObj.value !== undefined ? engagementObj.value : (summary?.delayed !== undefined ? summary.delayed : 0);

  const completedPct = efficiencyObj.value !== undefined ? efficiencyObj.value : (summary?.completed_percentage !== undefined ? summary.completed_percentage : 0);
  const inProgressPct = deliveryObj.value !== undefined ? deliveryObj.value : (summary?.in_progress_percentage !== undefined ? summary.in_progress_percentage : 0);
  const delayedPct = engagementObj.value !== undefined ? engagementObj.value : (summary?.delayed_percentage !== undefined ? summary.delayed_percentage : 0);

  const rawScoreChange = teamAvgKpiObj.change !== undefined ? teamAvgKpiObj.change : (summary?.score_change !== undefined && summary?.score_change !== null ? summary.score_change : 0);
  const direction = teamAvgKpiObj.trend || summary?.score_change_direction || (rawScoreChange < 0 ? "DECREASE" : rawScoreChange > 0 ? "INCREASE" : "STABLE");

  const isDecrease = direction === "DECREASE" || direction === "DOWN" || rawScoreChange < 0;
  const isIncrease = direction === "INCREASE" || direction === "UP" || (rawScoreChange > 0 && !isDecrease);

  const strokeColor = "#000000";
  const gradientColor = "#1e293b";

  const currentMonthName = dashboard?.month || dashboard?.current_month || summary?.month || new Date().toLocaleString('en-US', { month: 'long' });

  const rawUpcomingList = (Array.isArray(upcomingRecurring) && upcomingRecurring.length > 0)
    ? upcomingRecurring
    : (dashboard?.upcoming_recurring || dashboard?.upcomingRecurring || []);
  const upcomingList = rawUpcomingList.slice(0, 5);

  return (
    <div className="w-full">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">KPI Dashboard</h1>
          <p className="text-slate-500 text-sm">
            Leadership activity & KPI metrics tracking
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full md:w-auto">
          <Link to="/kpi/create" className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 text-white rounded-xl text-xs sm:text-sm font-bold hover:bg-slate-800 transition-colors whitespace-nowrap shrink-0 shadow-xs">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            <span>Create work</span>
          </Link>
        </div>
      </div>

      {/* Dark Stats Bar */}
      <div className="bg-[#111111] rounded-2xl p-5 sm:p-6 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 sm:gap-6 text-white shadow-lg">
        <div className="flex-1 w-full sm:w-auto">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Your KPI Score</div>
          <div className="flex items-baseline gap-3 mb-0.5">
            <div className="text-3xl sm:text-4xl font-semibold">{totalPoints}</div>
            <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isDecrease ? 'text-rose-400 bg-rose-500/10' : isIncrease ? 'text-emerald-500 bg-emerald-500/10' : 'text-slate-400 bg-slate-500/10'
              }`}>
              {isDecrease ? (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline><polyline points="17 18 23 18 23 12"></polyline></svg>
              ) : isIncrease ? (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
              ) : null}
              {isDecrease ? (rawScoreChange < 0 ? `${rawScoreChange}%` : `-${rawScoreChange}%`) : isIncrease ? `+${rawScoreChange}%` : `0%`}
            </div>
          </div>
          <div className="text-xs sm:text-sm text-slate-400">
            vs. previous period
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-8 w-full md:w-auto md:flex md:flex-1 md:items-center md:border-l border-white/10 pt-4 md:pt-0 border-t md:border-t-0 border-white/10 md:pl-10">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 whitespace-nowrap">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></div>
              <span className="truncate">Completed</span>
            </div>
            <div className="text-xl sm:text-3xl font-light mb-0.5 flex items-baseline gap-1 sm:gap-2">
              <span>{completedCount}</span>
              <span className="text-[10px] sm:text-xs font-medium text-emerald-400">({completedPct}%)</span>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 whitespace-nowrap">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></div>
              <span className="truncate">In Progress</span>
            </div>
            <div className="text-xl sm:text-3xl font-light mb-0.5 flex items-baseline gap-1 sm:gap-2">
              <span>{inProgressCount}</span>
              <span className="text-[10px] sm:text-xs font-medium text-amber-400">({inProgressPct}%)</span>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 whitespace-nowrap">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></div>
              <span className="truncate">Delayed</span>
            </div>
            <div className="text-xl sm:text-3xl font-light mb-0.5 flex items-baseline gap-1 sm:gap-2">
              <span>{delayedCount}</span>
              <span className="text-[10px] sm:text-xs font-medium text-rose-400">({delayedPct}%)</span>
            </div>
          </div>
        </div>

        {/* Commented out View report button
        <div className="w-full md:w-auto pt-3 md:pt-0 border-t md:border-t-0 border-white/10 md:border-l md:pl-6">
          <Link to="/kpi/reports" className="flex items-center justify-center md:justify-start gap-2 px-4 py-2.5 bg-white text-slate-900 rounded-xl text-xs sm:text-sm font-semibold hover:bg-slate-100 transition-colors w-full md:w-auto">
            <span>View report</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </Link>
        </div>
        */}
      </div>

      {/* ROW 1: Trend Graph (Wider: 7 cols) + Leaderboard (5 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6 items-stretch">

        {/* KPI Score Trend Graph (Left: 7 cols / ~58% width) */}
        <div className="lg:col-span-7 flex flex-col">
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-6 flex-1 flex flex-col justify-between">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-4">
              <div>
                <h2 className="text-base font-bold text-slate-900">KPI score trend</h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  {trendPeriod === "month"
                    ? "Monthly scores · trend analysis"
                    : trendPeriod === "quarter"
                      ? "Quarterly scores · trend analysis"
                      : "Yearly scores · trend analysis"}
                </p>
              </div>
              <div className="flex items-center p-1 bg-slate-100 rounded-xl w-full sm:w-auto justify-between sm:justify-start">
                <button
                  type="button"
                  onClick={() => handlePeriodChange("month")}
                  className={`px-3 sm:px-4 py-1 text-xs transition-all duration-150 ${trendPeriod === "month"
                    ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                    : "font-medium text-slate-500 hover:text-slate-900"
                    }`}
                >
                  Month
                </button>
                <button
                  type="button"
                  onClick={() => handlePeriodChange("quarter")}
                  className={`px-3 sm:px-4 py-1 text-xs transition-all duration-150 ${trendPeriod === "quarter"
                    ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                    : "font-medium text-slate-500 hover:text-slate-900"
                    }`}
                >
                  Quarter
                </button>
                <button
                  type="button"
                  onClick={() => handlePeriodChange("year")}
                  className={`px-3 sm:px-4 py-1 text-xs transition-all duration-150 ${trendPeriod === "year"
                    ? "bg-white text-slate-900 rounded-lg shadow-2xs font-bold"
                    : "font-medium text-slate-500 hover:text-slate-900"
                    }`}
                >
                  Year
                </button>
              </div>
            </div>
            <div className="h-64 sm:h-72 w-full flex-1 min-h-[250px]">
              {trendData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-slate-500">
                  No trend data available.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 10, right: 25, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={gradientColor} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={gradientColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                      dy={10}
                      interval={trendPeriod === "month" ? 2 : 0}
                      padding={{ left: 10, right: 15 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTrendTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="score"
                      stroke={strokeColor}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorScore)"
                      activeDot={{ r: 6, fill: strokeColor, stroke: '#fff', strokeWidth: 2 }}
                      dot={{ r: 4, fill: '#fff', stroke: strokeColor, strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Leaderboard (Right: 5 cols / ~42% width) */}
        <div className="lg:col-span-5 flex flex-col">
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-6 flex-1 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-5 border-b border-slate-100/80 pb-4">
                <div>
                  <h2 className="text-base font-bold text-slate-900 tracking-tight">Leaderboard</h2>
                  <p className="text-xs text-slate-400 font-medium mt-0.5">{currentMonthName} · all leaders</p>
                </div>
                <button
                  onClick={() => navigate("/kpi/leaderboard")}
                  className="flex items-center gap-1 px-3.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
                >
                  View all
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>

              <div className="space-y-1.5">
                {leaderboardData.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400 font-medium">
                    No leaderboard data available.
                  </div>
                ) : (
                  leaderboardData.slice(0, 5).map((leader, idx) => {
                    const rankNum = leader.rank !== undefined ? leader.rank : (idx + 1);

                    const getLeaderFullName = (l = {}) => {
                      if (l.full_name && l.full_name.trim()) return l.full_name.trim();
                      const first = l.first_name || "";
                      const last = l.last_name || "";
                      const combined = `${first} ${last}`.trim();
                      if (combined) return combined;
                      if (l.name && l.name.trim()) return l.name.trim();
                      if (l.email && l.email.includes("@")) {
                        const username = l.email.split("@")[0];
                        return username
                          .replace(/[._-]/g, " ")
                          .split(" ")
                          .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
                          .join(" ")
                          .replace(/\s+tblr$/i, "")
                          .trim();
                      }
                      return "Team Member";
                    };

                    const fullName = getLeaderFullName(leader);
                    const parts = fullName.split(" ").filter(Boolean);
                    const initials = parts.length >= 2
                      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
                      : (fullName.slice(0, 2) || "TM").toUpperCase();

                    const role = leader.designation || leader.designation_details?.name || leader.role || "Team Member";
                   const pointsValue = leader.monthly_score ?? 0;

                    const loggedInUser = userInfoData || userInfo?.userInfo || userInfo || {};
                    const currentUserId = loggedInUser._id || loggedInUser.id || "";
                    const currentUserEmail = (loggedInUser.email || "").toLowerCase().trim();

                    const isCurrentUser = Boolean(
                      leader.is_current_user ||
                      leader.isCurrentUser ||
                      (currentUserId && String(leader.user_id || leader._id || leader.id || "") === String(currentUserId)) ||
                      (currentUserEmail && (leader.email || "").toLowerCase().trim() === currentUserEmail)
                    );

                    let changeText = "";
                    let changeIsPositive = true;

                    if (leader.score_change !== undefined && leader.score_change !== null) {
                      const sc = leader.score_change;
                      if (sc > 0) {
                        changeText = `+${sc}`;
                        changeIsPositive = true;
                      } else if (sc < 0) {
                        changeText = `${sc}`;
                        changeIsPositive = false;
                      } else {
                        changeText = "0";
                        changeIsPositive = true;
                      }
                    } else if (leader.change) {
                      changeText = String(leader.change);
                      changeIsPositive = !leader.negative && !changeText.startsWith("-");
                    } else if (leader.movement) {
                      changeText = leader.movement;
                      changeIsPositive = true;
                    }

                    const getAvatarColorClass = (nameStr, i) => {
                      const s = (nameStr || "").toUpperCase();
                      if (s.includes("JERRY") || s.includes("ARJUN") || s.includes("MOHAMED")) return "bg-[#1d5bbd]";
                      if (s.includes("SHIBIN")) return "bg-[#147b56]";
                      if (s.includes("AFSAL") || s.includes("CUCKOO")) return "bg-[#ab6919]";
                      if (s.includes("LISTON") || s.includes("ATHIRA")) return "bg-[#6558e8]";
                      if (s.includes("HAFIZ") || s.includes("AKHIL") || s.includes("ANPU")) return "bg-[#c73838]";
                      const colors = ["bg-[#1d5bbd]", "bg-[#147b56]", "bg-[#ab6919]", "bg-[#6558e8]", "bg-[#c73838]"];
                      return colors[i % colors.length];
                    };

                    const avatarBgClass = getAvatarColorClass(fullName, idx);

                    return (
                      <div
                        key={leader.user_id || leader._id || idx}
                        className={`flex items-center gap-3 px-3 py-2.5 transition-all ${isCurrentUser
                          ? "bg-amber-50 border-l-4 border-amber-500 rounded-r-2xl my-1 shadow-2xs font-semibold"
                          : "hover:bg-slate-50/80 rounded-xl"
                          }`}
                      >
                        {/* Rank Indicator / Crown */}
                        <div className="w-6 text-center shrink-0 flex items-center justify-center">
                          {rankNum === 1 ? (
                            <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-2xs shrink-0" title="Rank 1">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
                              </svg>
                            </div>
                          ) : (
                            <span className="text-xs font-semibold text-slate-400">{rankNum}</span>
                          )}
                        </div>

                        {/* User Avatar */}
                        <div className={`w-9 h-9 rounded-full ${avatarBgClass} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                          {initials}
                        </div>

                        {/* User Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-slate-900 truncate">{fullName}</span>
                            {isCurrentUser && (
                              <span className="px-2 py-0.5 rounded-full bg-slate-900 text-amber-300 text-[9px] font-black uppercase tracking-wider shrink-0 shadow-2xs">
                                YOU
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 font-medium truncate">{role}</div>
                        </div>

                        {/* Score & Points */}
                        <div className="text-right shrink-0">
                          <div className="text-base font-bold text-slate-900 tracking-tight">{pointsValue}</div>
                          {changeText && (
                            <div className={`text-xs font-bold ${changeIsPositive ? "text-[#1b8354]" : "text-[#c73838]"}`}>
                              {changeText}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 2: Activity by Category + Recent Activity (Equal 50/50 Width) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6 items-stretch">

        {/* Activity by Category (Left: 6 cols / 50%) */}
        <div className="lg:col-span-6 flex flex-col">
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900">Activity by category</h2>
              <p className="text-xs text-slate-400 mt-0.5 mb-6">KPI points by work type · {currentMonthName}</p>

              {/* Stacked Progress Bar */}
              <div className="w-full h-3.5 rounded-full overflow-hidden flex mb-8 bg-slate-100">
                {displayCategoryData
                  .filter((cat) => (cat.percentage > 0 || cat.points > 0))
                  .map((cat, idx) => {
                    const hex = cat.hexColor || cat.colorObj?.hex || "#4F46E5";
                    const segWidth = cat.percentage > 0 ? cat.percentage : (cat.points > 0 ? 5 : 0);
                    return (
                      <div
                        key={idx}
                        className="h-full first:rounded-l-full last:rounded-r-full transition-all"
                        style={{ width: `${segWidth}%`, backgroundColor: hex }}
                        title={`${cat.name}: ${cat.points} pts (${cat.percentage}%)`}
                      ></div>
                    );
                  })}
              </div>

              {/* Legend Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
                {displayCategoryData.map((cat, idx) => {
                  const hex = cat.hexColor || cat.colorObj?.hex || "#4F46E5";
                  return (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-3 h-3 rounded-xs shrink-0" style={{ backgroundColor: hex }}></div>
                        <span className="text-xs font-medium text-slate-700 truncate">{cat.name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <span className="text-xs font-bold text-slate-900 font-mono">{cat.points}</span>
                        <span className="text-xs font-normal text-slate-400 w-9 text-right font-mono">{cat.percentage}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity (Right: 6 cols / 50%) */}
        <div className="lg:col-span-6 flex flex-col">
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-6 flex-1 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-5 border-b border-slate-100/80 pb-4">
                <div>
                  <h2 className="text-base font-bold text-slate-900 tracking-tight">Recent activity</h2>
                  <p className="text-xs text-slate-400 font-medium mt-0.5">What you and your team shipped</p>
                </div>
                <button
                  onClick={() => navigate("/kpi/activity-log")}
                  className="flex items-center gap-1 px-3.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
                >
                  Activity log
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>

              <div className="space-y-3.5">
                {flattenedActivities.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400 font-medium">
                    No recent activity found.
                  </div>
                ) : (
                  flattenedActivities.slice(0, 5).map((act, idx) => {
                    let actorName = act.actor?.full_name || act.actor?.name || act.name || "Member";
                    const nameParts = actorName.trim().split(" ");
                    const initials = nameParts.length >= 2
                      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
                      : actorName.slice(0, 2).toUpperCase();

                    const ptsVal = act.points !== undefined ? act.points : act.pts;

                    let timeDisplay = "";
                    if (act.created_at || act.createdAt || act.date || act.time) {
                      const d = new Date(act.created_at || act.createdAt || act.date || act.time);
                      if (!isNaN(d.getTime())) {
                        const diffMins = Math.floor((new Date() - d) / (1000 * 60));
                        if (diffMins < 1) timeDisplay = "Just now";
                        else if (diffMins < 60) timeDisplay = `${diffMins}m ago`;
                        else if (diffMins < 1440) timeDisplay = `${Math.floor(diffMins / 60)}h ago`;
                        else timeDisplay = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      }
                    }

                    const avatarColor = getUserAvatarColor(act.actor?._id || actorName, idx);
                    let actionText = "";
                    const eventTypeUpper = String(act.event_type || act.action || act.type || "").toUpperCase();

                    const parseValueObj = (val) => {
                      if (!val) return {};
                      if (typeof val === 'object') return val;
                      if (typeof val === 'string') {
                        const trimmed = val.trim();
                        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                          try {
                            const parsed = JSON.parse(trimmed);
                            if (parsed && typeof parsed === 'object') return parsed;
                          } catch (e) {}
                        }
                      }
                      return {};
                    };

                    const prevValObj = parseValueObj(act.old_value || act.previous_value);
                    const newValObj = parseValueObj(act.new_value);

                    const getGroupVal = (val, valDetails) => {
                      if (valDetails && valDetails.group_id) {
                        const detailVal = valDetails.group_id;
                        if (typeof detailVal === "object" && detailVal !== null) {
                          if (detailVal.group_name || detailVal.name || detailVal.title) {
                            return String(detailVal.group_name || detailVal.name || detailVal.title).trim();
                          }
                        }
                      }
                      if (val === undefined || val === null) return null;
                      if (typeof val === "object" && val !== null) {
                        const extracted = val.group_name || val.name || val.title || val.group_id || val.group;
                        if (extracted === undefined || extracted === null) return null;
                        return getGroupVal(extracted);
                      }
                      const str = String(val).trim();
                      if (str === "" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
                      return str.replace(/"/g, "");
                    };

                    const getAssigneeName = (valObj, valDetails) => {
                      const details = (valObj && valObj.assigned_to_details) || (valDetails && valDetails.assigned_to_details) || (valDetails && (valDetails.assigned_to || valDetails.assignee));
                      if (details && Array.isArray(details) && details.length > 0) {
                        return details
                          .map((u) => {
                            if (typeof u === "object" && u !== null) {
                              return u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email || "";
                            }
                            return String(u);
                          })
                          .filter(Boolean)
                          .join(", ")
                          .replace(/"/g, "");
                      }
                      const target = (valObj && (valObj.assigned_to || valObj.assignee)) || (valDetails && (valDetails.assigned_to || valDetails.assignee)) || valObj;
                      if (!target) return "";
                      if (Array.isArray(target)) {
                        return target
                          .map((u) => {
                            if (typeof u === "object" && u !== null) {
                              return u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email || "";
                            }
                            return String(u);
                          })
                          .filter(Boolean)
                          .join(", ")
                          .replace(/"/g, "");
                      }
                      if (typeof target === "object" && target !== null) {
                        return (target.full_name || `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.name || target.email || "").replace(/"/g, "");
                      }
                      return String(target).replace(/"/g, "");
                    };

                    const wId = (act.work_id || act.task_details?.work_id || newValObj.work_id || prevValObj.work_id || "").replace(/"/g, "").trim();
                    const wName = (act.work_name || act.task_details?.task_name || act.target || newValObj.work_name || newValObj.task_name || prevValObj.work_name || prevValObj.task_name || "").replace(/"/g, "").trim();

                    const workTitle = (wId && wName && wId.toLowerCase() !== wName.toLowerCase())
                      ? `${wId} (${wName})`
                      : (wId || wName || "work");

                    const isTaskDeleted =
                      eventTypeUpper === "TASK_DELETED" ||
                      eventTypeUpper === "TASK_DELETE" ||
                      eventTypeUpper === "WORK_DELETED" ||
                      eventTypeUpper === "WORK_DELETE" ||
                      eventTypeUpper === "DELETE" ||
                      eventTypeUpper.includes("DELETE") ||
                      act.is_deleted === true ||
                      newValObj.is_deleted === true ||
                      newValObj.status_name === "DELETE" ||
                      String(act.action || "").toUpperCase().includes("DELETE");

                    const targetId = isTaskDeleted ? null : (act.task_id || act.work_id);

                    if (isTaskDeleted) {
                      actionText = `deleted task ${workTitle}`;
                    } else if (eventTypeUpper === "DESCRIPTION_CHANGE" || eventTypeUpper === "TASK_DESCRIPTION_CHANGE" || "task_description" in prevValObj || "task_description" in newValObj) {
                      actionText = `changed description of ${workTitle}`;
                    } else if (eventTypeUpper === "ASSIGNMENT_CHANGE" || eventTypeUpper === "ASSIGNEE_CHANGE" || "assigned_to" in prevValObj || "assigned_to" in newValObj || "assigned_to_details" in prevValObj || "assigned_to_details" in newValObj) {
                      const prevAssignee = getAssigneeName(prevValObj, act.previous_value_details);
                      const newAssignee = getAssigneeName(newValObj, act.new_value_details);

                      if (prevAssignee && newAssignee && prevAssignee !== newAssignee) {
                        actionText = `changed assignee of ${workTitle} from ${prevAssignee} to ${newAssignee}`;
                      } else if (newAssignee) {
                        actionText = `assigned ${workTitle} to ${newAssignee}`;
                      } else if (prevAssignee) {
                        actionText = `unassigned ${workTitle} from ${prevAssignee}`;
                      } else {
                        actionText = `updated assignee of ${workTitle}`;
                      }
                    } else if (
                      eventTypeUpper === "DUE_DATE_CHANGE" ||
                      eventTypeUpper === "DUE_DATE_UPDATED" ||
                      eventTypeUpper === "TASK_DUE_DATE_CHANGE" ||
                      eventTypeUpper === "DUE_DATE" ||
                      eventTypeUpper.includes("DUE_DATE") ||
                      eventTypeUpper.includes("END_DATE") ||
                      "task_end_date" in prevValObj ||
                      "task_end_date" in newValObj ||
                      "due_date" in prevValObj ||
                      "due_date" in newValObj
                    ) {
                      const formatDate = (strVal) => {
                        if (!strVal) return "";
                        const d = new Date(strVal);
                        return !isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : String(strVal).replace(/"/g, "");
                      };
                      const prevDate = formatDate(prevValObj.task_end_date || prevValObj.due_date || (typeof act.previous_value !== 'object' && !String(act.previous_value).startsWith('{') ? act.previous_value : ''));
                      const newDate = formatDate(newValObj.task_end_date || newValObj.due_date || (typeof act.new_value !== 'object' && !String(act.new_value).startsWith('{') ? act.new_value : ''));

                      if (prevDate && newDate && prevDate !== newDate) {
                        actionText = `changed due date of ${workTitle} from ${prevDate} to ${newDate}`;
                      } else if (newDate) {
                        actionText = `set due date of ${workTitle} to ${newDate}`;
                      } else {
                        actionText = `updated due date of ${workTitle}`;
                      }
                    } else if (
                      eventTypeUpper.includes("GROUP") ||
                      "group_id" in prevValObj ||
                      "group_id" in newValObj ||
                      "group_name" in prevValObj ||
                      "group_name" in newValObj
                    ) {
                      const prevGroup = getGroupVal(prevValObj.group_id ?? prevValObj.group_name ?? prevValObj.group ?? (typeof act.previous_value !== 'object' && !String(act.previous_value).startsWith('{') ? act.previous_value : null), act.previous_value_details);
                      const newGroup = getGroupVal(newValObj.group_id ?? newValObj.group_name ?? newValObj.group ?? (typeof act.new_value !== 'object' && !String(act.new_value).startsWith('{') ? act.new_value : null), act.new_value_details);

                      if (!prevGroup && newGroup) {
                        actionText = `moved ${workTitle} to group ${newGroup}`;
                      } else if (prevGroup && newGroup && prevGroup !== newGroup) {
                        actionText = `moved ${workTitle} from group ${prevGroup} to ${newGroup}`;
                      } else if (prevGroup && !newGroup) {
                        actionText = `removed ${workTitle} from group ${prevGroup}`;
                      } else if (newGroup) {
                        actionText = `moved ${workTitle} to group ${newGroup}`;
                      }
                    } else if (eventTypeUpper.includes("COMMENT") || (act.description && act.description.toLowerCase().includes("comment")) || newValObj.comment_text || newValObj.comment) {
                      const commentContent = (act.comment || act.message || act.text || newValObj.comment_text || newValObj.comment || "").replace(/"/g, "").trim();
                      actionText = `added a comment on ${workTitle}${commentContent ? `: "${commentContent}"` : ""}`;
                    } else if (eventTypeUpper === "CREATE" || eventTypeUpper === "WORK_CREATE" || eventTypeUpper === "TASK_CREATE") {
                      actionText = `created task ${workTitle}`;
                    } else if (eventTypeUpper === "STATUS_CHANGE" || eventTypeUpper === "STATUS_CHANGED" || "status_name" in prevValObj || "status_name" in newValObj) {
                      const prevStatus = (act.old_value?.status_name || act.previous_value?.status_name || prevValObj.status_name || "").replace(/"/g, "");
                      const newStatus = (act.new_value?.status_name || act.status_name || newValObj.status_name || "").replace(/"/g, "");
                      if (prevStatus && newStatus && prevStatus !== newStatus) {
                        actionText = `changed status of ${workTitle}: ${prevStatus} → ${newStatus}`;
                      } else if (newStatus) {
                        actionText = `updated status of ${workTitle} to ${newStatus}`;
                      } else {
                        actionText = `updated status of ${workTitle}`;
                      }
                    } else if (eventTypeUpper === "PRIORITY_CHANGE" || eventTypeUpper === "PRIORITY_CHANGED" || "task_priority" in prevValObj || "task_priority" in newValObj) {
                      const prevPrio = (prevValObj.task_priority || prevValObj.priority || "").replace(/"/g, "");
                      const newPrio = (act.new_value?.task_priority || act.new_value?.priority || newValObj.task_priority || newValObj.priority || "").replace(/"/g, "");
                      if (prevPrio && newPrio && prevPrio !== newPrio) {
                        actionText = `changed priority of ${workTitle} from ${prevPrio} to ${newPrio}`;
                      } else if (newPrio) {
                        actionText = `changed priority of ${workTitle} to ${newPrio}`;
                      } else {
                        actionText = `updated priority of ${workTitle}`;
                      }
                    } else if (eventTypeUpper === "KPI_AWARD" || eventTypeUpper === "AWARD") {
                      const pts = act.points !== undefined ? act.points : (newValObj.assigned_kpi_points || act.pts || 0);
                      actionText = `awarded ${pts} KPI points for ${workTitle}`;
                    } else if (eventTypeUpper === "KPI_DEDUCTION" || eventTypeUpper === "DEDUCTION") {
                      const pts = act.points !== undefined ? act.points : (newValObj.assigned_kpi_points || act.pts || 0);
                      actionText = `deducted ${pts} KPI points for ${workTitle}`;
                    } else if (act.description) {
                      let desc = act.description.replace(/\s*(?:→|->|=>)\s*/g, " - ");
                      if (desc.toLowerCase().startsWith(actorName.toLowerCase())) {
                        desc = desc.slice(actorName.length).trim();
                      }
                      actionText = desc;
                    } else {
                      actionText = `updated ${workTitle}`;
                    }

                    actionText = actionText.replace(/performed Comment Added/gi, "added a comment");
                    actionText = actionText.replace(/performed\s+/gi, "updated ");
                    actionText = actionText.replace(/_/g, " ");
                    actionText = actionText.replace(/\s*(?:→|->|=>)\s*/g, " - ");
                    actionText = actionText.replace(/"/g, "");

                    const formatBadgeText = (type) => {
                      if (!type) return "";
                      const upper = String(type).toUpperCase();
                      if (upper === "CREATE" || upper === "WORK_CREATE" || upper === "TASK_CREATE") return "Created";
                      if (upper === "STATUS_CHANGE" || upper === "STATUS_CHANGED") return "Status Change";
                      if (upper === "PRIORITY_CHANGE" || upper === "PRIORITY_CHANGED") return "Priority";
                      if (upper === "UPDATE" || upper === "WORK_UPDATE") return "Updated";
                      return upper.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                    };

                    return (
                      <div
                        key={act.activity_id || act._id || idx}
                        className={`flex gap-3 items-start p-1.5 rounded-lg transition-colors ${targetId ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
                        onClick={() => {
                          if (targetId) navigate(`/kpi/works/${targetId}`);
                        }}
                      >
                        <div className={`w-7 h-7 rounded-full ${avatarColor} text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 shadow-2xs`}>
                          {initials}
                        </div>
                        <div className="flex-1 text-[13px] leading-snug">
                          <div className="text-slate-800">
                            <span className="font-bold text-slate-900 mr-1.5">{actorName}</span>
                            <span className="text-slate-700 font-medium">{actionText}</span>
                          </div>
                          {timeDisplay && (
                            <div className="text-[11px] text-slate-400 mt-0.5">{timeDisplay}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Recurring Works Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Upcoming recurring works</h2>
            <p className="text-sm text-slate-500 mt-0.5">Auto-scheduled active recurring task templates</p>
          </div>
          <Link
            to="/kpi/recurring"
            className="px-4 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors !no-underline"
          >
            Manage recurring
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-[#f7f6f3]">
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Cadence</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">KPI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(!upcomingList || upcomingList.length === 0) ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-sm text-slate-500 font-medium">
                    No upcoming recurring works scheduled
                  </td>
                </tr>
              ) : (
                upcomingList.map((row, idx) => {
                  const title = getSafeStringVal(row.task_name) || getSafeStringVal(row.title) || getSafeStringVal(row.name) || "Untitled Work";

                  const typeObj = row.kpi_type || row.task_type || row.type || (typeof row.task_kpi_type_id === "object" ? row.task_kpi_type_id : null);
                  let typeName = getSafeStringVal(typeObj) || getSafeStringVal(row.task_type_name) || getSafeStringVal(row.kpi_type_name);
                  if (!typeName && typeof row.task_kpi_type_id === "string" && Array.isArray(taskTypes)) {
                    const foundType = taskTypes.find(t => String(t._id || t.id) === String(row.task_kpi_type_id));
                    if (foundType) {
                      typeName = foundType.name || foundType.kpi_type_name || foundType.type;
                    }
                  }
                  if (!typeName) typeName = "Task";

                  let cadence = getSafeStringVal(row.cadence) || getSafeStringVal(row.recurrence_pattern);
                  if (!cadence) {
                    const freq = getSafeStringVal(row.recurrence_frequency) || getSafeStringVal(row.frequency) || getSafeStringVal(row.recurrence);
                    if (freq) {
                      const lower = freq.toLowerCase();
                      cadence = lower === "daily" ? "Daily" : lower === "weekly" ? "Weekly" : lower === "monthly" ? "Monthly" : lower === "quarterly" ? "Quarterly" : lower === "yearly" ? "Yearly" : freq;
                    } else {
                      cadence = "Recurring";
                    }
                  }

                  const ptsVal = (row.kpi_type?.score !== undefined)
                    ? row.kpi_type.score
                    : (row.assigned_kpi_points !== undefined ? row.assigned_kpi_points : (row.points !== undefined ? row.points : row.pts));
                  let ptsDisplay = "+0";
                  if (ptsVal !== undefined && ptsVal !== null) {
                    const safePts = typeof ptsVal === "object" ? (ptsVal.points || ptsVal.score || ptsVal.val) : ptsVal;
                    if (typeof safePts === "string" && (safePts.startsWith("+") || safePts.startsWith("-"))) {
                      ptsDisplay = safePts;
                    } else {
                      const num = Number(safePts);
                      ptsDisplay = isNaN(num) ? String(safePts) : (num >= 0 ? `+${num}` : `${num}`);
                    }
                  }

                  const taskId = (typeof row.recurrence_id === "string" && row.recurrence_id)
                    ? row.recurrence_id
                    : ((typeof row._id === "string" && row._id)
                      ? row._id
                      : ((typeof row.task_id === "string" && row.task_id)
                        ? row.task_id
                        : ((typeof row.work_id === "string" && row.work_id) ? row.work_id : (row.id || null))));

                  return (
                    <tr
                      key={taskId || idx}
                      className={`hover:bg-slate-50/50 transition-colors group ${taskId ? "cursor-pointer" : ""}`}
                      onClick={() => {
                        if (taskId) {
                          navigate(`/kpi/recurring/${taskId}`, { state: { work: row } });
                        }
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg bg-slate-100/80 border border-slate-200/70 text-slate-600 flex items-center justify-center shrink-0 shadow-2xs">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="23 4 23 10 17 10"></polyline>
                              <polyline points="1 20 1 14 7 14"></polyline>
                              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                          </div>
                          <span className="text-sm font-semibold text-slate-900">{title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold tracking-wide">{typeName}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{cadence}</td>
                      <td className="px-6 py-4">
                        <span className="bg-slate-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{ptsDisplay}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default KPIDashboard;
